// The Operations workbench's shared vocabulary: the words its badges print, the
// edges §7 permits a detail card to offer, and the reading of a refused request.
//
// The state machine and the kind vocabulary live on the server
// (platform/assignment.go's assignmentTransitions, the generated enums), and this
// module restates them rather than reading them from OPTIONS: the door is the
// only place the machine is enforced, and a workbench that offered an edge the
// server refuses would be a worse failure than one that waits for a newer
// command. Every value here is the enum's own spelling, so a mismatch is a
// compile error rather than a mislabelled badge.

import { formatDate, formatDateOnly } from "@/format";
import type {
	AssignmentKind,
	AssignmentState,
	MapReviewSiteRow,
	RouteState,
	SyncAssignment,
	SyncRoute,
} from "@/type/api";

/** The six kinds, in the order operator decision 1 lists them. */
export const KIND_OPTIONS: { value: AssignmentKind; label: string }[] = [
	{ value: "set_trap", label: "Set trap" },
	{ value: "collect_trap", label: "Collect trap" },
	{ value: "inspect", label: "Inspect" },
	{ value: "abate", label: "Abate" },
	{ value: "treat", label: "Treat" },
	{ value: "custom", label: "Custom" },
];

/** §7's states, in the order the machine walks them. */
export const STATE_OPTIONS: { value: AssignmentState; label: string }[] = [
	{ value: "draft", label: "Draft" },
	{ value: "dispatched", label: "Dispatched" },
	{ value: "in_progress", label: "In progress" },
	{ value: "deferred", label: "Deferred" },
	{ value: "completed", label: "Completed" },
	{ value: "cancelled", label: "Cancelled" },
];

/** The label a kind badge prints. A row that predates the vocabulary reads as
 * Unclassified rather than as a blank badge — it is still work somebody was
 * asked to do. */
export function kindLabel(kind: AssignmentKind | null): string {
	if (kind === null) return "Unclassified";
	return KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
}

/** The badge colour a kind takes; an unclassified row is grey, not coloured
 * like work the vocabulary describes. */
export function kindBadgeClass(kind: AssignmentKind | null): string {
	switch (kind) {
		case "set_trap":
		case "collect_trap":
			return "bg-primary";
		case "inspect":
			return "bg-info text-dark";
		case "abate":
			return "bg-warning text-dark";
		case "treat":
			return "bg-success";
		case "custom":
			return "bg-secondary";
		default:
			return "bg-light text-dark border";
	}
}

/** The badge colour a Work row's origin takes, so a Lead reads the same wherever
 * it is listed — the Work queue, the Route shortcut and the Assignment form's
 * bulk door. */
export function originBadge(origin: string): string {
	switch (origin) {
		case "lead":
			return "text-bg-primary";
		case "work_order":
			return "text-bg-warning";
		default:
			return "text-bg-secondary";
	}
}

/** The states that are still work to do: §7's machine has exactly two terminal
 * states, so every other one is outstanding. The Assignments pane lists these
 * and the rail's badge counts them — one predicate, so the count and the rows
 * can never disagree. */
export function isOpenAssignment(assignment: SyncAssignment): boolean {
	switch (assignment.state) {
		case "draft":
		case "dispatched":
		case "in_progress":
		case "deferred":
			return true;
		case "completed":
		case "cancelled":
			return false;
	}
}

/** The label a state badge prints. */
export function stateLabel(state: AssignmentState): string {
	return STATE_OPTIONS.find((option) => option.value === state)?.label ?? state;
}

/** The badge colour a state takes. Terminal states read differently from the
 * ones the machine can still move. */
export function stateBadgeClass(state: AssignmentState): string {
	switch (state) {
		case "draft":
			return "bg-secondary";
		case "dispatched":
			return "bg-primary";
		case "in_progress":
			return "bg-info text-dark";
		case "deferred":
			return "bg-warning text-dark";
		case "completed":
			return "bg-success";
		case "cancelled":
			return "bg-dark";
	}
}

/**
 * §7's table, edge for edge (doc/reference/ontology/Nidus Core Ontology.md:693-700):
 * draft → dispatched | cancelled, dispatched → in_progress | cancelled,
 * in_progress → deferred | completed, deferred → in_progress. Completed and
 * cancelled are terminal.
 */
const TRANSITIONS: Record<AssignmentState, AssignmentState[]> = {
	draft: ["dispatched", "cancelled"],
	dispatched: ["in_progress", "cancelled"],
	in_progress: ["deferred", "completed"],
	deferred: ["in_progress"],
	completed: [],
	cancelled: [],
};

/** The states this Assignment may move into from where it is. */
export function transitionTargets(state: AssignmentState): AssignmentState[] {
	return TRANSITIONS[state] ?? [];
}

/**
 * The message a failed request carries, read the way the rest of the app reads
 * it (the envelope's `message`). Nothing is rewritten: a refused completion's
 * message names the missing evidence, and it is the server's words that tell the
 * operator what to record.
 */
export function describeError(e: unknown): string {
	if (e && typeof e === "object" && "response" in e) {
		const { response } = e;
		if (response && typeof response === "object" && "data" in response) {
			const { data } = response;
			if (
				data &&
				typeof data === "object" &&
				"message" in data &&
				typeof data.message === "string" &&
				data.message.trim() !== ""
			) {
				return data.message;
			}
		}
	}
	if (e instanceof Error && e.message !== "") return e.message;
	return "The request failed.";
}

function pad(value: number, width = 2): string {
	return String(value).padStart(width, "0");
}

/** A `datetime-local` value for a Date in the operator's own zone, which is the
 * only zone the form field states. */
export function localInputValue(date: Date): string {
	return (
		`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
		`T${pad(date.getHours())}:${pad(date.getMinutes())}`
	);
}

/**
 * The default window: today's 08:00 to the same hour tomorrow. Decision 9 makes
 * the window arbitrary — a Route is not a calendar day — so the default is one
 * day only because a day is the unit an office thinks in, and the operator moves
 * either end freely.
 */
export function defaultWindow(): { start: string; end: string } {
	const start = new Date();
	start.setHours(8, 0, 0, 0);
	const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
	return { start: localInputValue(start), end: localInputValue(end) };
}

/** A `datetime-local` value as the wire's instant, or null when the field is
 * blank. The browser reads the field in the operator's zone, and the wire
 * carries UTC, so the conversion is the field's own. */
export function wireInstant(value: string): string | null {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toISOString();
}

/**
 * A Site named the way the rest of the app names it (MapReview.vue's
 * popupSiteTitle, Instances.vue's siteLabel): the label when it reads as a name,
 * else the address, else the id. The workbench reads the Site ledger's QUERY
 * projection, which carries the display address as one string beside the
 * representative point the map draws the Site at.
 */
export function siteLabel(site: MapReviewSiteRow): string {
	if (site.label && /[A-Za-z]/.test(site.label)) return site.label;
	if (site.address) return site.address;
	return `Site #${site.id}`;
}

/** The target an Assignment names, in the words the panes and the details
 * sheet print. The API guarantees exactly one, so the first match is the
 * target. */
export function targetLabel(
	assignment: SyncAssignment,
	sites: MapReviewSiteRow[],
): string {
	if (assignment.targetSiteId !== null) {
		const site = sites.find(
			(candidate) => candidate.id === assignment.targetSiteId,
		);
		return site ? siteLabel(site) : `Site #${assignment.targetSiteId}`;
	}
	if (assignment.targetFeatureId !== null) {
		return `Feature #${assignment.targetFeatureId}`;
	}
	if (assignment.targetFacilitatorId !== null) {
		return `Facilitator #${assignment.targetFacilitatorId}`;
	}
	if (assignment.targetGeometry !== null) return "Drawn geometry";
	return "No target";
}

/** A technician's name, falling back to the id for a user the session's user list
 * does not carry. */
export function technicianLabel(
	technicianId: number,
	users: { id: number; display_name: string }[],
): string {
	const user = users.find((candidate) => candidate.id === technicianId);
	return user ? user.display_name : `Technician #${technicianId}`;
}

/** An Assignment's stated window. An assignment may state neither end, one end,
 * or both — decision 9 makes the window arbitrary — so the text says which. */
export function assignmentWindowText(assignment: SyncAssignment): string {
	if (assignment.startAt === null && assignment.endAt === null) {
		return "No window stated";
	}
	const start =
		assignment.startAt === null
			? "any time"
			: formatDate(new Date(assignment.startAt));
	const end =
		assignment.endAt === null
			? "any end"
			: formatDate(new Date(assignment.endAt));
	return `${start} → ${end}`;
}

/** A Route's operational window, both ends always stated by the door. */
export function routeWindowText(route: SyncRoute): string {
	return `${formatDate(new Date(route.windowStart))} → ${formatDate(new Date(route.windowEnd))}`;
}

/** The day a Route's window opens, which is how the Routes pane groups work
 * without pretending a Route is a calendar day (decision 9). */
export function routeDay(route: SyncRoute): string {
	return formatDateOnly(new Date(route.windowStart));
}

/** The badge colour a Route state takes; the same vocabulary §8 walks, read
 * the way the Assignment badges are. */
export function routeBadgeClass(state: RouteState): string {
	switch (state) {
		case "draft":
			return "bg-secondary";
		case "dispatched":
			return "bg-primary";
		case "active":
			return "bg-info text-dark";
		case "completed":
			return "bg-success";
		case "cancelled":
			return "bg-dark";
	}
}
